import { useCallback, useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { inputCls, textareaCls, labelCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { slugify } from "@/lib/slugify"
import { LoadState } from "@/components/ui/LoadState"

type InstitutionType = "educational" | "government" | "private_company" | "ngo" | "other"

const INSTITUTION_TYPE_OPTIONS: { value: InstitutionType; label: string }[] = [
  { value: "educational", label: "Institución educativa" },
  { value: "government", label: "Entidad gubernamental" },
  { value: "private_company", label: "Empresa privada" },
  { value: "ngo", label: "ONG" },
  { value: "other", label: "Otra" },
]

type InstitutionForm = {
  name: string
  slug: string
  description: string
  type: InstitutionType
  contact_name: string
  contact_email: string
}

const initialForm: InstitutionForm = {
  name: "",
  slug: "",
  description: "",
  type: "other",
  contact_name: "",
  contact_email: "",
}

export default function InstitutionFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<InstitutionForm>(initialForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const res = await api.get("/academy/seedbeds/admin/institutions?limit=200&page=1")
      const found = (res.data?.data?.institutions ?? []).find((i: { id: string }) => i.id === id)
      if (!found) {
        setError("Organización no encontrada")
        return
      }
      setForm({
        name: found.name,
        slug: found.slug,
        description: found.description ?? "",
        type: found.type ?? "other",
        contact_name: found.contactName ?? "",
        contact_email: found.contactEmail ?? "",
      })
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  function goBack() {
    navigate("/programs/institutions")
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      if (isEdit && id) {
        await api.put(`/academy/seedbeds/admin/institutions/${id}`, {
          name: form.name,
          description: form.description || undefined,
          type: form.type,
          contact_name: form.contact_name || undefined,
          contact_email: form.contact_email || undefined,
        })
      } else {
        await api.post("/academy/seedbeds/admin/institutions", {
          name: form.name,
          slug: slugify(form.name),
          description: form.description || undefined,
          type: form.type,
          contact_name: form.contact_name || undefined,
          contact_email: form.contact_email || undefined,
        })
      }
      qc.invalidateQueries({ queryKey: ["server-table", "/academy/seedbeds/admin/institutions"] })
      goBack()
    } catch (err) {
      setError(getErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Organizaciones", to: "/programs/institutions" },
        isEdit ? "Editar" : "Nueva",
      ]}
      title={isEdit ? "Editar organización" : "Nueva organización"}
      subtitle="Base para programas y semilleros"
      tabs={[]}
      metrics={[]}
    >
      {loading ? (
        <LoadState />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Identidad</h3>
              <div>
                <label className={labelCls}>Nombre de la organización *</label>
                <input
                  required
                  autoFocus
                  placeholder="Nombre de la organización"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      name: e.target.value,
                      slug: isEdit ? f.slug : slugify(e.target.value),
                    }))
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Tipo de organización</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as InstitutionType }))}
                  className={inputCls}
                >
                  {INSTITUTION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="rounded-lg bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 dark:bg-gray-800">
                Slug: <span className="font-mono">{form.slug || "—"}</span> (autogenerado, no editable)
              </p>
            </div>

            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Contacto</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Nombre de contacto</label>
                  <input
                    placeholder="Nombre de contacto"
                    value={form.contact_name}
                    onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Email de contacto</label>
                  <input
                    type="email"
                    placeholder="Email de contacto"
                    value={form.contact_email}
                    onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Descripción</label>
                <textarea
                  rows={6}
                  placeholder="Descripción (opcional)"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className={textareaCls}
                />
              </div>
            </div>
          </div>

          {error ? (
            <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={btnSecondary}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={btnSave}>
              {saving ? "Guardando..." : isEdit ? "Guardar" : "Crear"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
